/**
 * What the user WANTS, kept apart from what the server will currently publish.
 *
 * Its own module with no imports, so a plain `node --test` can drive the whole
 * state machine — `session-store.ts` pulls `expo-*` and cannot be imported
 * there. The same reason `fix-age.ts` is separate, and for the same payoff:
 * the sequence this owns (visible -> refused -> retry -> visible) is exactly
 * the one that broke, and it was untestable while it lived inside the store.
 *
 * ## The bug this exists to prevent
 *
 * When `POST /presence/join` is refused for a stale fix, an earlier fix set
 * `visibilityStatus: "invisible"` so the header would stop claiming Live. That
 * was right about the header and wrong about everything else: `refreshVenue`
 * decides between `join()` and `setVisibility(false)` by reading that same
 * field, so the next refresh took the user's own intent as a decision to hide
 * and actively published them as invisible. Retrying with a perfect fix left
 * them dark until they toggled the switch by hand — and it removed the one
 * mercy the original bug had, which was that it healed itself on the next
 * focus.
 *
 * Two facts, never one:
 *
 * - `desired` — the switch the user set. Only they may change it.
 * - `blocked` — the server's refusal about the CURRENT fix, which clears as
 *   soon as a publish succeeds.
 *
 * "Live" is the conjunction. Everything else follows from keeping them apart.
 */

/** The reasons the server refuses to publish a position. Mirrors `PublishRefusal`. */
export type RefusalReason =
  | "no_coordinates"
  | "accuracy"
  | "stale"
  | "integrity_hold";

export type VisibilityDesire = "visible" | "invisible";

export const REFUSAL_REASONS: readonly RefusalReason[] = [
  "no_coordinates",
  "accuracy",
  "stale",
  "integrity_hold",
];

export function isRefusalReason(value: unknown): value is RefusalReason {
  return (
    typeof value === "string" &&
    (REFUSAL_REASONS as readonly string[]).includes(value)
  );
}

/**
 * The refusal reason carried by a `visibility_needs_location` response body.
 *
 * Returns `"stale"` for a body that says only that it is a visibility refusal
 * without naming a reason — the refusal is real either way, and the fallback
 * that asks for a fresh reading is the one that is never actively wrong.
 */
export function refusalReasonFrom(body: unknown): RefusalReason | null {
  if (typeof body !== "object" || body === null) return null;
  const { code, reason } = body as { code?: unknown; reason?: unknown };
  if (code !== "visibility_needs_location") return null;
  return isRefusalReason(reason) ? reason : "stale";
}

/**
 * Is this person actually discoverable right now?
 *
 * The only thing the "Live" pill may be built from. Reading `desired` alone is
 * what let the header promise visibility the server had refused, and a user who
 * believes they are discoverable when they are not is the worst failure this
 * screen has.
 */
export function isLive(state: {
  desired: VisibilityDesire;
  blocked: RefusalReason | null;
}): boolean {
  return state.desired === "visible" && state.blocked === null;
}

/**
 * Should the client publish this person, given what they asked for?
 *
 * Deliberately ignores `blocked`: a retry exists precisely to find out whether
 * the refusal still stands, and gating the attempt on the last answer would
 * make the block permanent until the user toggled the switch.
 */
export function shouldPublish(state: {
  desired: VisibilityDesire;
  cuesManuallyPaused: boolean;
  /**
   * Taken deliberately, and deliberately unused. Declaring it is what makes
   * "the block does not gate the attempt" a property a test can break: with the
   * field absent, a version that consulted it would read `undefined` from every
   * caller and pass.
   */
  blocked: RefusalReason | null;
}): boolean {
  return state.desired === "visible" && !state.cuesManuallyPaused;
}

/**
 * The screen state a refusal reason should produce.
 *
 * Here rather than in the store so it can be driven directly: the store cannot
 * be imported by `node --test`, and a mapping that flattens four distinct
 * reasons into one is invisible from the outside — every case still renders
 * *something*.
 *
 * `integrity_hold` deliberately shares the stale state. The server keeps that
 * refusal vague on purpose — naming the rule that caught it tells whoever is
 * probing it what to change — and the hold lapses on its own within minutes, so
 * "we need a fresh reading" is both true and the right thing to wait for.
 */
export function screenStateForRefusal(
  reason: RefusalReason,
): "location_stale" | "location_imprecise" | "location_timeout" {
  switch (reason) {
    case "accuracy":
      return "location_imprecise";
    case "no_coordinates":
      return "location_timeout";
    case "stale":
    case "integrity_hold":
      return "location_stale";
  }
}

/** State after the server refuses to publish. Intent is never touched. */
export function afterRefusal(
  state: { desired: VisibilityDesire },
  reason: RefusalReason,
): { desired: VisibilityDesire; blocked: RefusalReason } {
  return { desired: state.desired, blocked: reason };
}

/** State after a publish succeeds: whatever was blocking is no longer true. */
export function afterPublish(state: { desired: VisibilityDesire }): {
  desired: VisibilityDesire;
  blocked: null;
} {
  return { desired: state.desired, blocked: null };
}
